/**
 * Autorouter data hooks (docs/design/AUTOROUTER-SPEC.md).
 *
 * `useRoutedNoteIdsQuery` is the one every title surface reads: a small, cached set of the
 * note ids currently showing the "Autorouted" badge. Each surface checks membership locally
 * instead of enriching every tree/search row. The presets + activity hooks back the Settings
 * panel; the mutations drive undo + the per-folder toggle.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  fetchAutorouterActivity,
  fetchAutorouterPresets,
  fetchRoutedNoteIds,
  setNotebookAutorouter,
  undoAutorouting,
} from '@/api';
import type { AutorouterActivityItem, AutorouterPreset, NotebookId } from '@/types';
import { useErrorHandler } from './useErrorHandler';

const ROUTED_IDS_KEY = ['autorouter', 'routed-note-ids'] as const;
const PRESETS_KEY = ['autorouter', 'presets'] as const;
const ACTIVITY_KEY = ['autorouter', 'activity'] as const;

/**
 * The set of note ids currently badged "Autorouted". Returned as a `Set` for O(1) membership.
 * Cached generously (it changes only when a note is routed or a human moves a routed note);
 * the badge tolerates brief staleness. Tolerant of a 404 (backend without Autorouter) — it
 * resolves to an empty set so the UI degrades to "no badges".
 */
export function useRoutedNoteIdsQuery(): UseQueryResult<Set<string>, Error> {
  return useQuery({
    queryKey: ROUTED_IDS_KEY,
    staleTime: 60_000,
    retry: false,
    queryFn: async () => {
      try {
        return new Set(await fetchRoutedNoteIds());
      } catch {
        // Backend may predate the feature, or the endpoint may 404 — no badges, no error noise.
        return new Set<string>();
      }
    },
  });
}

export function useAutorouterPresetsQuery(): UseQueryResult<
  { version: string; defaultId: string; presets: AutorouterPreset[] },
  Error
> {
  const { handleError } = useErrorHandler();
  return useQuery({
    queryKey: PRESETS_KEY,
    staleTime: Infinity, // shipped constants — never change within a session
    queryFn: async () => {
      try {
        return await fetchAutorouterPresets();
      } catch (err) {
        handleError(err);
        throw err;
      }
    },
  });
}

export function useAutorouterActivityQuery(
  limit = 50,
): UseQueryResult<AutorouterActivityItem[], Error> {
  return useQuery({
    queryKey: [...ACTIVITY_KEY, limit],
    retry: false,
    queryFn: async () => {
      try {
        return await fetchAutorouterActivity(limit);
      } catch {
        return [];
      }
    },
  });
}

/** "Send back to Inbox" — moves the note back, clears the badge, refreshes activity + badges. */
export function useUndoAutoroutingMutation(): UseMutationResult<unknown, Error, string> {
  const qc = useQueryClient();
  const { handleError } = useErrorHandler();
  return useMutation<unknown, Error, string>({
    mutationFn: (logId: string) => undoAutorouting(logId),
    onError: (err) => {
      handleError(err);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ACTIVITY_KEY });
      void qc.invalidateQueries({ queryKey: ROUTED_IDS_KEY });
      void qc.invalidateQueries({ queryKey: ['notebooks', 'notes'] });
    },
  });
}

/** Flip a folder's router permission (the tree glow toggle). Refreshes the notebook tree. */
export function useSetNotebookAutorouterMutation(): UseMutationResult<
  unknown,
  Error,
  { notebookId: NotebookId; enabled: boolean }
> {
  const qc = useQueryClient();
  const { handleError } = useErrorHandler();
  return useMutation<unknown, Error, { notebookId: NotebookId; enabled: boolean }>({
    mutationFn: ({ notebookId, enabled }) => setNotebookAutorouter(notebookId, enabled),
    onError: (err) => {
      handleError(err);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['notebooks', 'children'] });
    },
  });
}
